import { EffectLabels } from './EffectLabels'
import { CountBadges } from './CountBadges'
import { pieceLabel } from './piece-label'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { automaticAction, actionTargets, type ActionOption, type Projection, type ResolutionStatus } from '@v2/sdk'
import type { TableController } from '@v2/table'
import { PileBrowser } from './PileBrowser'
import { Panel } from './Panel'
import { Rulebook } from './Rulebook'
import { ComponentBrowser } from './ComponentBrowser'
import { PeekInspector } from './PeekInspector'
import { EnginePanel } from './EnginePanel'
import { ScenePicker } from './ScenePicker'
import { CameraControls } from './CameraControls'
import { ActionList } from './ActionList'
import { TurnChip } from './TurnChip'
import { PlayerStrip } from './PlayerStrip'
import { Icon, type IconName } from './icons'
import { useTable } from './useTable'
import { HandControls } from './HandControls'
import './styles.css'
import { interfaceSoundEvents } from './interface-sounds'
import { NotificationFeed, type TableNotification } from './NotificationFeed'
import { isYourTurn, startsYourTurn, type TurnSnapshot } from './turn-notification'

export interface TabletopProps {
  readonly notifications?: readonly TableNotification[]
  /** Suppress turn feedback while disconnected or waiting for room approval. */
  readonly turnNotifications?: boolean
  readonly historyTools?: ReactNode
  readonly onPing?: (position: import("@v2/contracts").Vec3) => void
  readonly resolution?: ResolutionStatus
  readonly projection: Projection
  /** `values` accompanies a set-controlled action; `action.command` is already the exact command. */
  readonly onAction: (action: ActionOption, values?: readonly string[]) => void
  readonly onController?: (controller: TableController | null) => void
  readonly onRestart?: () => void
  /** Trusted host controls are separate from the player-facing game projection. */
  readonly engineTools?: ReactNode
  /** Room membership, invitations and bot controls supplied by an online host. */
  readonly roomTools?: ReactNode
  /** Connection/session status supplied by the host, separate from game rules. */
  readonly hostStatus?: ReactNode
  /** A transient host message shown above the table. */
  readonly alert?: ReactNode
  /** Where the leave button goes. Omitted for hotseat tables without a lobby. */
  readonly homeHref?: string
  /** Prevent duplicate or disconnected commands while preserving inspection and navigation. */
  readonly actionsDisabled?: boolean
}
type OpenPanel = 'rules' | 'components' | 'engine' | 'room' | 'pile' | null
const HINT_KEY = 'tabletop:hint-seen'

/**
 * The table fills the window. Everything else is a small edge overlay that earns its
 * pixels: turn at the top left, players at the top right, camera at the bottom left,
 * tools at the bottom right, and the current choice in the middle of the bottom edge.
 */
export function Tabletop({ notifications = [], turnNotifications = true, historyTools, resolution: hostResolution, onPing, projection: hostProjection, onAction, onController, onRestart, engineTools, roomTools, hostStatus, alert, homeHref, actionsDisabled: hostActionsDisabled = false }: TabletopProps) {
  const resolution = hostResolution?.revision === hostProjection.revision ? hostResolution : undefined
  const actionsDisabled = hostActionsDisabled || resolution?.phase === 'settling'
  // A scheduled resolver is the host's move. Hide it from every surface (3D pieces, piece
  // menus, pile and component browsers) so a manual click can never race the host.
  const automatic = resolution?.phase === 'scheduled' ? automaticAction(hostProjection.actions) : undefined
  const projection = useMemo(() => automatic
    ? { ...hostProjection, actions: hostProjection.actions.filter(action => action.id !== automatic.id) }
    : hostProjection, [hostProjection, automatic])
  const canvas = useRef<HTMLCanvasElement>(null)
  const stage = useRef<HTMLDivElement>(null)
  const alertSlot = useRef<HTMLDivElement>(null)
  const table = useTable(canvas, projection, action => { if (!actionsDisabled && action.id !== automatic?.id) onAction(action) }, onController, onPing)
  const [panel, setPanel] = useState<OpenPanel>(null)
  const [pileId, setPileId] = useState<string | null>(null)
  const pile = projection.scene.pieces.find(piece => piece.id === pileId && !piece.hidden)
  const browse = (id: string) => { closeInspect(); controller.current?.closeMenu(); setPileId(id); setPanel('pile') }
  const inspectionReturn = useRef<OpenPanel>(null)
  const [fallbackInspect, setFallbackInspect] = useState<string | null>(null)
  const [outcomeDismissed, setOutcomeDismissed] = useState(false)
  const [hint, setHint] = useState(() => { try { return !localStorage.getItem(HINT_KEY) } catch { return true } })
  const [pingsMuted, setPingsMuted] = useState(false)
  const { controller, state, status } = table
  const previousTurn = useRef<TurnSnapshot | null>(null)
  const ownTurn = isYourTurn(hostProjection)
  const [showTurnNotice, setShowTurnNotice] = useState(true)
  useEffect(() => {
    setShowTurnNotice(ownTurn)
    if (!ownTurn) return
    const timer = window.setTimeout(() => setShowTurnNotice(false), 8000)
    return () => window.clearTimeout(timer)
  }, [ownTurn, hostProjection.viewer])
  useEffect(() => {
    if (turnNotifications && startsYourTurn(previousTurn.current, hostProjection)) controller.current?.playTurnSound()
    previousTurn.current = hostProjection
  }, [hostProjection, turnNotifications, controller])
  const prominent = (notification: TableNotification) => notification.placement
    ? notification.placement === 'alert' : notification.level === 'warning' || notification.level === 'error'
  const feed = notifications.filter(prominent)
  const updates = notifications.filter(notification => !prominent(notification))
  if (turnNotifications && ownTurn && showTurnNotice && !resolution) feed.push({
    id: 'your-turn', level: 'success', placement: 'alert', title: 'Your turn', onDismiss: () => setShowTurnNotice(false),
  })
  const hasAlerts = feed.length > 0 || !!alert
  useEffect(() => {
    const element = alertSlot.current
    if (!element) return
    const update = () => stage.current?.style.setProperty('--tt-alert-height', `${element.getBoundingClientRect().height}px`)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(element)
    return () => { observer.disconnect(); stage.current?.style.removeProperty('--tt-alert-height') }
  }, [hasAlerts])
  useEffect(() => { controller.current?.setActionsDisabled(actionsDisabled) }, [actionsDisabled, controller, status.kind])
  useEffect(() => {
    if (!hint || status.kind !== 'ready') return
    const timer = setTimeout(() => { setHint(false); try { localStorage.setItem(HINT_KEY, '1') } catch { /* optional */ } }, 9000)
    return () => clearTimeout(timer)
  }, [hint, status.kind])
  const inspectedId = state?.inspectedId ?? fallbackInspect
  const inspected = projection.scene.pieces.find(piece => piece.id === inspectedId)
  const peeked = projection.scene.pieces.find(piece => piece.id === state?.peekedId)
  const menu = state?.menu
  const menuPiece = menu && projection.scene.pieces.find(piece => piece.id === menu.objectId)
  const menuZone = menu && projection.scene.zones.find(zone => zone.id === menu.objectId)
  const menuLabel = menuPiece ? pieceLabel(menuPiece) : menuZone?.label
  const menuActions = projection.actions.filter(action => menu && actionTargets(action).includes(menu.objectId))
  // Clicking a public pile browses it directly; its cards are chosen inside the sheet.
  const browsable = menuPiece && menuPiece.contents && !menuPiece.hidden ? menuPiece.id : null
  useEffect(() => { if (browsable) browse(browsable) }, [browsable])
  const globalActions = projection.actions.filter(action => action.presentation === 'global' || actionTargets(action).length === 0)
  const focus = (id: string) => { controller.current?.focus(id); setPanel(null) }
  const closeInspect = () => { controller.current?.inspect(null); setFallbackInspect(null) }
  const inspect = (id: string) => {
    inspectionReturn.current = panel
    setPanel(null)
    if (controller.current) controller.current.inspect(id)
    else setFallbackInspect(id)
  }
  const returnFromInspection = () => {
    closeInspect()
    setPanel(inspectionReturn.current)
    inspectionReturn.current = null
  }
  const activate = (action: ActionOption, values?: readonly string[]) => {
    if (actionsDisabled || action.id === automatic?.id) return
    closeInspect()
    controller.current?.closeMenu()
    controller.current?.clearSelection()
    onAction(action, values)
  }
  // The table owns the draft of a set choice so physical clicks and checkboxes stay in step.
  const selection = (controlId: string) => controller.current ? {
    values: state?.selection?.controlId === controlId ? state.selection.values : [],
    toggle: (id: string, value: string) => controller.current?.toggleSelection(id, value),
  } : undefined
  const openPanel = (next: OpenPanel) => {
    closeInspect()
    controller.current?.closeMenu()
    setPanel(current => current === next ? null : next)
  }

  useEffect(() => { setFallbackInspect(null); setOutcomeDismissed(false) }, [projection.viewer, projection.revision])
  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setPanel(null); setFallbackInspect(null); controller.current?.inspect(null); controller.current?.closeMenu() }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [controller])

  const ready = status.kind === 'ready'
  const quiet = !panel && !inspected
  const tool = (name: string, icon: IconName, onClick: () => void, extra?: { active?: boolean; disabled?: boolean; pressed?: boolean }) =>
    <button type="button" className={`tt-rail-button${extra?.active ? ' is-active' : ''}`} aria-label={name} title={name} aria-pressed={extra?.pressed ?? extra?.active} disabled={extra?.disabled} onClick={onClick}><Icon name={icon}/></button>

  return <div {...interfaceSoundEvents(sound => controller.current?.playInterfaceSound(sound))} className={`tt-stage${peeked ? ' is-peeking' : ''}${state?.hand.zone ? ' has-held-hand' : ''}`} ref={stage} aria-label={`${projection.game.name} table`}>
    <EffectLabels controller={controller}/>
    {ready && quiet && !peeked && <CountBadges key={projection.viewer} projection={projection} controller={controller}/>}
    <canvas ref={canvas} className="tt-canvas" aria-label={`${projection.game.name} interactive 3D table. WASD pans, arrow keys orbit, plus and minus zoom, T shows top view, Home fits the table. Use the Components button for keyboard-accessible pieces and actions.`} tabIndex={0} data-testid="table-canvas"/>

    <div className="tt-corner tt-corner-top-left">
      {homeHref && <a className="tt-rail-button tt-leave" href={homeHref} aria-label="Leave the table" title="Leave the table"><Icon name="home"/></a>}
      <TurnChip resolution={resolution} projection={projection} updates={updates.length > 0 ? <NotificationFeed notifications={updates} compact/> : undefined} status={<><span className="tt-live" role="status" data-testid="renderer-status" data-state={status.kind}><span aria-hidden="true"/>{projection.outcome ? 'Finished' : hostStatus ?? 'Local table'}</span></>}/>
    </div>
    <div className="tt-corner tt-corner-top-right"><PlayerStrip projection={projection}/></div>
    {hasAlerts && <div ref={alertSlot} className="tt-alert-slot"><NotificationFeed notifications={feed}>{alert}</NotificationFeed></div>}

    {ready && quiet && <CameraControls controller={controller.current} mode={state?.cameraMode ?? 'orbit'} places={projection.scene.camera?.places}/>}
    {ready && state?.hand && quiet && <HandControls hand={state.hand} controller={controller.current}/>}
    {!ready && <div className={`tt-canvas-status${status.kind === 'error' ? ' is-error' : ''}`} role="status" data-testid="renderer-message">
      <Icon name={status.kind === 'error' ? 'engine' : 'leaf'} size={30}/>
      <h3>{status.kind === 'starting' ? 'Setting the table…' : 'The 3D table is unavailable'}</h3>
      <p>{status.kind === 'starting' ? 'Preparing the cards and finding the light.' : status.message}</p>
      {status.kind === 'error' && <><p>You can still read the rules and play through Components.</p><button className="tt-primary-button" onClick={table.retry}>Try again</button></>}
    </div>}
    {ready && quiet && hint && !projection.outcome && <div className="tt-hint" aria-hidden="true">Click a highlighted piece · Hold ⌘ or Alt to inspect · Drag to {state?.cameraMode ?? 'orbit'} · Shift-click to ping</div>}

    {menu && menuLabel && quiet && !browsable && <div className="tt-context-menu" role="dialog" aria-label={`${menuLabel} actions`} style={{ left: Math.max(12, Math.min(menu.x, (stage.current?.clientWidth ?? 500) - 312)), top: Math.max(12, Math.min(menu.y, (stage.current?.clientHeight ?? 500) - 350)) }}>
      <div className="tt-context-heading"><h3>{menuLabel}</h3><button className="tt-icon-button" aria-label="Close piece actions" onClick={() => controller.current?.closeMenu()}><Icon name="close" size={15}/></button></div>
      <ActionList key={`${projection.viewer}:${projection.revision}:${menu.objectId}`} actions={menuActions} onAction={activate} disabled={actionsDisabled} compact selection={selection}/>
      {menuPiece ? <button className="tt-context-inspect" onClick={() => inspect(menuPiece.id)}><Icon name="eye" size={14}/>Take a closer look</button> : <button className="tt-context-inspect" onClick={() => focus(menu.objectId)}><Icon name="overview" size={14}/>Show this area</button>}
    </div>}
    {(globalActions.length > 0 || !!projection.unavailableActions?.length) && !projection.outcome && <div className={`tt-global-actions${globalActions.length > 4 ? ' has-many-choices' : ''}`} aria-label="Turn actions"><ActionList key={`${projection.viewer}:${projection.revision}`} actions={globalActions} onAction={activate} disabled={actionsDisabled} compact label="Turn choices" selection={selection}/>{projection.unavailableActions?.map(action => <div key={action.label} className="tt-unavailable-action"><button className="tt-primary-button" disabled title={action.reason}>{action.label}</button><p className="tt-empty">{action.reason}</p></div>)}</div>}

    {panel === 'pile' && <Panel title={pile?.label ?? 'Pile'} onClose={() => setPanel(null)}>{pile ? <PileBrowser key={`${projection.viewer}:${pile.id}`} piece={pile} actions={projection.actions} onAction={activate} disabled={actionsDisabled}/> : <p>This pile is no longer available.</p>}</Panel>}
    {panel === 'rules' && <Panel title="How to play" onClose={() => setPanel(null)}><Rulebook projection={projection}/></Panel>}
    {panel === 'components' && <Panel title="Components" onClose={() => setPanel(null)}><ComponentBrowser key={projection.viewer} projection={projection} onBrowse={browse} onInspect={inspect} onFocus={focus} onAction={activate} disabled={actionsDisabled}/></Panel>}
    {panel === 'room' && roomTools && <Panel title="Room" onClose={() => setPanel(null)}>{roomTools}</Panel>}
    {panel === 'engine' && <Panel title="Engine room" onClose={() => setPanel(null)}><EnginePanel projection={projection} state={state} controller={controller.current}>{engineTools}</EnginePanel></Panel>}
    {inspected && <PeekInspector key={`${projection.viewer}:${inspected.id}`} piece={inspected} onClose={returnFromInspection} fallback={!ready}/>}
    {peeked && <PeekInspector key={`${projection.viewer}:${peeked.id}`} piece={peeked}/>}
    {projection.outcome && !outcomeDismissed && quiet && <div className="tt-outcome" role="dialog" aria-label="Game complete"><div className="tt-outcome-card"><span className="tt-eyebrow">Game over</span><h2>{projection.outcome.title}</h2><p>{projection.outcome.detail}</p><div className="tt-outcome-actions">{onRestart && <button className="tt-primary-button" onClick={onRestart}><Icon name="reset" size={15}/>Play again</button>}<button className="tt-secondary-button" onClick={() => setOutcomeDismissed(true)}>Explore the final table</button></div></div></div>}
    {projection.outcome && outcomeDismissed && <div className="tt-global-actions"><button className="tt-secondary-button" onClick={() => setOutcomeDismissed(false)}>View game result</button>{onRestart && <button className="tt-primary-button" onClick={onRestart}>Play again</button>}</div>}

    {historyTools && <div className="tt-history-tools" aria-label="Game history">{historyTools}</div>}
    <div className="tt-rail" aria-label="Table tools">
      {tool('Overview', 'overview', () => { controller.current?.resetCamera(); setPanel(null); closeInspect() }, { pressed: undefined })}
      {tool('Rules', 'book', () => openPanel('rules'), { active: panel === 'rules' })}
      {tool('Components', 'layers', () => openPanel('components'), { active: panel === 'components' })}
      {roomTools && tool('Room', 'people', () => openPanel('room'), { active: panel === 'room' })}
      <ScenePicker environment={state?.environment ?? projection.scene.environment ?? { preset: 'ocean', motion: true }} reducedMotion={state?.reducedMotion ?? false} disabled={!ready} onChange={preset => controller.current?.setEnvironment(preset)} onMotionChange={enabled => controller.current?.setEnvironmentMotion(enabled)}/>
      {tool(state?.paused ? 'Resume motion' : 'Pause motion', state?.paused ? 'play' : 'pause', () => controller.current?.setPaused(!state?.paused), { disabled: !ready, pressed: Boolean(state?.paused) })}
      {tool(state?.soundsMuted ? 'Enable sound effects' : 'Mute sound effects', state?.soundsMuted ? 'muted' : 'sound', () => controller.current?.setSoundMuted(!state?.soundsMuted), { disabled: !ready, pressed: Boolean(state?.soundsMuted) })}
      {tool(pingsMuted ? 'Unmute pings' : 'Mute pings', pingsMuted ? 'bell-off' : 'bell', () => { controller.current?.setPingMuted(!pingsMuted); setPingsMuted(!pingsMuted) }, { disabled: !ready, pressed: pingsMuted })}
      {tool('Engine', 'engine', () => openPanel('engine'), { active: panel === 'engine' })}
    </div>
  </div>
}
